/**
 * Formatage d'affichage.
 *
 * `formatIsoTime`/`formatIsoTimeSeconds` convertissent dans un fuseau — celui
 * donné explicitement (CDC §3.2 : un arrêt pourrait porter le sien), sinon le
 * fuseau **local du poste**. Corrigé 2026-09-18 (bug utilisateur : toutes les
 * heures affichées avec 2 h d'avance en été/CEST, 1 h en hiver/CET) — la
 * version précédente lisait les chiffres HH:MM **bruts de la chaîne ISO par
 * regex**, sans jamais construire de `Date` ni convertir. Le commentaire
 * d'origine (« respecter le fuseau porté par la donnée ») partait d'une
 * hypothèse jamais vérifiée : en pratique, TOUTES les `IsoDateTime` émises par
 * le backend sont de l'UTC pur (`Date.toISOString()`, suffixe `Z`) — vérifié
 * par Claude-2 sur l'ensemble des routes qui les servent. « Préserver l'offset
 * de la chaîne » revenait donc à « toujours afficher l'UTC brut », faux dès
 * qu'on n'est pas à UTC+0. Les horaires GTFS statiques (jour de service,
 * `"25:30:00"` au-delà de minuit) sont une représentation différente, jamais
 * passée à ces fonctions — non concernés.
 */

import type { PositionType } from "../contract/types";

/** Écart signé en `+M:SS` / `−M:SS` (tiret cadratin, style maquettes). */
export function formatDeviation(seconds: number): string {
  const sign = seconds < 0 ? "−" : "+";
  const abs = Math.abs(Math.round(seconds));
  const m = Math.floor(abs / 60);
  const s = abs % 60;
  return `${sign}${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Nomenclature Sentinelle de l'écart (décision utilisateur 2026-09-07) — à utiliser
 * partout (carte, file d'écarts, panneau véhicule…) :
 *  - avance (dès la 1re seconde, car **interdite**) ⇒ `A:MM` (`A:00` sous la minute) ;
 *  - retard sous la minute / pile à l'heure (**toléré**) ⇒ « À l'heure » ;
 *  - retard ⇒ `R:MM`.
 * MM = minutes (plancher), zéro-paddées.
 */
export function formatDeviationShort(seconds: number): string {
  if (seconds < 0) return `A:${String(Math.floor(-seconds / 60)).padStart(2, "0")}`;
  const min = Math.floor(seconds / 60);
  return min === 0 ? "À l'heure" : `R:${String(min).padStart(2, "0")}`;
}

/**
 * Même nomenclature `A` / `R` mais à la **seconde** (`A:MM:SS` / `R:MM:SS`) —
 * pour les colonnes d'impact fin (projection arrêt par arrêt, J.6) où l'on veut
 * lire l'écart exact, pas seulement la minute. `R:00:00` pile à l'heure.
 */
export function formatDeviationHms(seconds: number): string {
  const abs = Math.abs(Math.round(seconds));
  const mm = String(Math.floor(abs / 60)).padStart(2, "0");
  const ss = String(abs % 60).padStart(2, "0");
  return `${seconds < 0 ? "A" : "R"}:${mm}:${ss}`;
}

/**
 * Couleurs fond / texte au **standard Sentinelle** (§E.3, décision utilisateur
 * 2026-09-07) : **avance = rouge** (interdite, infraction), **retard = vert**
 * (toléré sous le seuil), **à l'heure** (< 1 min de retard) = neutre.
 */
export function deviationColors(seconds: number): { bg: string; fg: string } {
  if (seconds < 0) return { bg: "#dc2626", fg: "#ffffff" };
  if (seconds >= 60) return { bg: "#15803d", fg: "#ffffff" };
  return { bg: "var(--sentinelle-scheme-main-ter)", fg: "var(--sentinelle-ink-2)" };
}

/** Durée en clair : `6 min 40`, `12 s`, `1 h 05`. */
export function formatDuration(seconds: number): string {
  const abs = Math.abs(Math.round(seconds));
  if (abs < 60) return `${abs} s`;
  const m = Math.floor(abs / 60);
  const s = abs % 60;
  if (m < 60) return s === 0 ? `${m} min` : `${m} min ${String(s).padStart(2, "0")}`;
  const h = Math.floor(m / 60);
  return `${h} h ${String(m % 60).padStart(2, "0")}`;
}

/**
 * Heure `HH:MM` depuis des secondes écoulées DEPUIS MINUIT LOCAL (horaire GTFS
 * ou `ReleveRisk.atSeconds`) — PAS un nombre d'heures borné à 24 : un service
 * commencé la veille peut dépasser minuit (CDC §3.2, « les horaires GTFS
 * dépassent 24:00:00 »). `28:05` pour un départ à 4h05 le lendemain, jamais
 * rabattu sur `04:05` — même convention que `services/ServicesScreen.tsx`.
 */
export function formatSecondsOfDay(seconds: number): string {
  const m = Math.round(seconds / 60);
  const h = Math.floor(m / 60);
  const mm = ((m % 60) + 60) % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

/** Heure `HH:MM:SS` dans un fuseau donné (celui de la donnée). */
export function formatClock(date: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

/** Heure `HH:MM` — fuseau donné, sinon local. `iso` invalide : rendue telle quelle. */
export function formatIsoTime(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

/** Heure `HH:MM:SS` — mêmes bases que `formatIsoTime`. */
export function formatIsoTimeSeconds(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : formatClock(date, timeZone);
}

export function formatPercent(fraction: number, digits = 1): string {
  return `${(fraction * 100).toFixed(digits).replace(".", ",")} %`;
}

const POSITION_TYPE_LABELS: Record<PositionType, string> = {
  gps: "GPS",
  estimated: "Estimée",
  scheduled: "Théorique",
  stale: "Figée",
};

export function positionTypeLabel(type: PositionType): string {
  return POSITION_TYPE_LABELS[type];
}

/** Jeton CSS de couleur pour un type de position (voir theme.css). */
export function positionTypeColorVar(type: PositionType): string {
  return `var(--sentinelle-pos-${type})`;
}

/**
 * Valeurs observées sur le flux GTFS-RT réel d'Astuce qui ne sont PAS un
 * numéro de parc bien qu'elles ne soient ni `null` ni vides : chaîne vide
 * (corrigé à la source côté ingest, `label: ""` traité comme absent) et un
 * simple `"?"` littéral. Ce dernier touchait 75 % des véhicules actifs le
 * 2026-09-04 ; Claude-2 a confirmé par décodage protobuf réel que Cityway ne
 * publie tout simplement jamais `vehicle.label` pour Astuce (pas un défaut
 * d'ingest) et a ajouté un repli serveur qui l'extrait de `vehicle.id`
 * (`TCAR`/`TAE`, dont le format l'encode — pas `TNI`, qui restera
 * parfois à `"?"` malgré le repli). Gardé ici en filet de sécurité : un
 * simple `??`/chaîne-vide ne rattrape ni l'un ni l'autre cas et laisserait
 * passer un badge trompeur.
 */
export function isAbsentVehicleNumber(displayNumber: string | null | undefined): boolean {
  if (!displayNumber) return true;
  const trimmed = displayNumber.trim();
  return trimmed === "" || trimmed === "?";
}

/** Numéro de parc à afficher, ou un tiret cadratin si absent (cf. `isAbsentVehicleNumber`). */
export function shortVehicleNumber(displayNumber: string | null | undefined): string {
  return isAbsentVehicleNumber(displayNumber) ? "—" : (displayNumber as string);
}

/** Numéro de parc à afficher, ou « ? » si absent/non identifié (dump utilisateur 2026-09-10). */
export function vehicleNumberOrUnidentified(displayNumber: string | null | undefined): string {
  return isAbsentVehicleNumber(displayNumber) ? "?" : (displayNumber as string);
}

export type RunMatchState = { text: string; matched: boolean };

/**
 * État de la sortie (« run », CDC §5.8) d'une course selon l'avancement du
 * rapprochement véhicule↔sortie (B6) :
 *  - `runId` présent (rapprochement établi) → le numéro de sortie ;
 *  - `runConfidence: "tentative"` → rapprochement en cours, pas encore de numéro ;
 *  - `runConfidence: null` → aucun rapprochement tenté (ex. navette : sa seule
 *    source d'horaire est le tableau de marche, pas de flux à rapprocher).
 * `matched` distingue le cas « on a un vrai numéro » pour l'affichage (couleur).
 */
export function runMatchState(
  runId: string | null,
  runConfidence: "established" | "tentative" | null,
): RunMatchState {
  if (runId) return { text: runId, matched: true };
  if (runConfidence === "tentative") return { text: "rapprochement en cours…", matched: false };
  return { text: "?", matched: false };
}

/**
 * Ancienneté relative (« il y a X ») — SEULE implémentation de l'app, reprise
 * ici depuis deux doublons (`AnticipationPanel`, `VehiclesScreen` — même
 * calcul, l'un en `Math.round`, l'autre en `Math.floor`, un plafond « heures »
 * différent) : préférer toujours celle-ci plutôt qu'en réécrire une troisième.
 * `Math.floor` (pas `Math.round`) : « il y a 0 s » plutôt que d'annoncer un
 * futur par arrondi. `now` explicite = testable et réutilisable dans un rendu
 * déjà daté (ex. `AnticipationEvent.since` au moment où la liste a été reçue).
 */
export function formatAgo(iso: string, now: number = Date.now()): string {
  const s = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 1000));
  if (s < 90) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 90) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 36) return `${h} h ${String(m % 60).padStart(2, "0")}`;
  return `${Math.floor(h / 24)} j`;
}
